/**
 * Gallery Hybrid Data Service
 * NOW NATIVE ONLY - PhotonLink support removed
 */

import { Firestore, collection, query, where, orderBy as firestoreOrderBy, getDocs } from 'firebase/firestore';
import { Album as FirebaseAlbum } from '@/models/album';
import { MediaItem as FirebaseMediaItem } from '@/models/media-item';
import { resolveThumbnailUrl } from '@/lib/youtube-thumbnail';

/**
 * Unified album type that can represent both sources
 */
export interface HybridAlbum {
  id: string;
  name: string;
  description?: string;
  coverUrl?: string;
  mediaCount: number;
  source: 'firebase';
  path: string;
  parentId?: string | null;
  depth: number;
  sortOrder?: number;
  sortName?: string | null;
  albumType?: 'root' | 'system' | 'field-flights' | 'member-personal' | 'custom' | 'photonlink';
  visibility?: 'public' | 'members' | 'officers' | 'private';
  photonlinkId?: never; // Removed
}

/**
 * Unified media type that can represent both sources
 */
export interface HybridMediaItem {
  id: string;
  title: string;
  description?: string;
  url: string;
  thumbnailUrl: string;
  width?: number;
  height?: number;
  isPanorama?: boolean;
  type: 'photo' | 'video';
  source: 'firebase';
  albumId: string;
  uploadedAt?: Date;
  uploaderName?: string;
  viewCount?: number;
  tags: string[];
}

/**
 * Get all root-level albums (Firebase only)
 */
export async function getHybridRootAlbums(firestore: Firestore): Promise<HybridAlbum[]> {
  const firebaseAlbums = await getFirebaseRootAlbums(firestore);
  // Sort before converting: sortName first (if present), then sortOrder, then name
  firebaseAlbums.sort((a, b) => {
    // If both have sortName, compare them
    if (a.sortName && b.sortName) {
      return a.sortName.localeCompare(b.sortName);
    }
    // sortName takes precedence if only one has it
    if (a.sortName && !b.sortName) return -1;
    if (!a.sortName && b.sortName) return 1;
    // Fall back to sortOrder
    const orderA = a.sortOrder ?? 0;
    const orderB = b.sortOrder ?? 0;
    if (orderA !== orderB) return orderA - orderB;
    return a.name.localeCompare(b.name);
  });
  return firebaseAlbums.map(convertFirebaseAlbumToHybrid);
}

/**
 * Get sub-albums for a specific album (Firebase only)
 */
export async function getHybridSubAlbums(
  firestore: Firestore,
  albumId: string
): Promise<HybridAlbum[]> {
  // Fetch Firebase sub-albums
  const firebaseAlbums = await getFirebaseSubAlbums(firestore, albumId);
  return firebaseAlbums.map(convertFirebaseAlbumToHybrid);
}

/**
 * Get media items for a specific album (Firebase only)
 */
export async function getHybridAlbumMedia(
  firestore: Firestore,
  albumId: string
): Promise<HybridMediaItem[]> {
  const firebaseMedia = await getFirebaseAlbumMedia(firestore, albumId);
  return firebaseMedia.map(convertFirebaseMediaToHybrid);
}

/**
 * Get all media from both sources (for unified gallery view)
 */
export async function getAllHybridMedia(
  firestore: Firestore,
  options?: {
    limit?: number;
    sortBy?: 'uploadedAt' | 'title';
    sortDir?: 'asc' | 'desc';
  }
): Promise<HybridMediaItem[]> {
  const firebaseMedia = await getFirebaseAllMedia(firestore, options);
  return firebaseMedia.map(convertFirebaseMediaToHybrid);
}

// Helper functions

async function getFirebaseRootAlbums(firestore: Firestore): Promise<FirebaseAlbum[]> {
  const albumsRef = collection(firestore, 'albums');
  // Note: Don't use orderBy('sortOrder') here because documents without the sortOrder
  // field will be excluded from results. Sorting is done in JavaScript in getHybridRootAlbums.
  const q = query(
    albumsRef,
    where('parentAlbumId', '==', null),
    where('isArchived', '==', false)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as FirebaseAlbum));
}

async function getFirebaseSubAlbums(firestore: Firestore, parentAlbumId: string): Promise<FirebaseAlbum[]> {
  const albumsRef = collection(firestore, 'albums');
  // Note: Don't use orderBy('sortOrder') here because documents without the sortOrder
  // field will be excluded from results. Sort in JavaScript instead.
  const q = query(
    albumsRef,
    where('parentAlbumId', '==', parentAlbumId),
    where('isArchived', '==', false)
  );

  const snapshot = await getDocs(q);
  // Sort by sortName (if present), then sortOrder, then by name
  const albums = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as FirebaseAlbum));
  return albums.sort((a, b) => {
    // If both have sortName, compare them
    if (a.sortName && b.sortName) {
      return a.sortName.localeCompare(b.sortName);
    }
    // sortName takes precedence if only one has it
    if (a.sortName && !b.sortName) return -1;
    if (!a.sortName && b.sortName) return 1;
    // Fall back to sortOrder
    const orderA = a.sortOrder ?? 0;
    const orderB = b.sortOrder ?? 0;
    if (orderA !== orderB) return orderA - orderB;
    return a.name.localeCompare(b.name);
  });
}

async function getFirebaseAlbumMedia(firestore: Firestore, albumId: string): Promise<FirebaseMediaItem[]> {
  const mediaRef = collection(firestore, 'media');
  const q = query(
    mediaRef,
    where('albumId', '==', albumId),
    firestoreOrderBy('uploadedAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as FirebaseMediaItem));
}

async function getFirebaseAllMedia(
  firestore: Firestore,
  options?: {
    limit?: number;
    sortBy?: 'uploadedAt' | 'title';
    sortDir?: 'asc' | 'desc';
  }
): Promise<FirebaseMediaItem[]> {
  const mediaRef = collection(firestore, 'media');
  const sortField = options?.sortBy || 'uploadedAt';
  const sortDir = options?.sortDir || 'desc';

  const q = query(
    mediaRef,
    firestoreOrderBy(sortField, sortDir)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as FirebaseMediaItem));
}

function convertFirebaseAlbumToHybrid(album: FirebaseAlbum): HybridAlbum {
  return {
    id: album.id,
    name: album.name,
    description: album.description || undefined,
    coverUrl: album.coverUrl || undefined,
    mediaCount: album.mediaCount,
    source: 'firebase',
    path: album.albumPath,
    parentId: album.parentAlbumId,
    depth: album.depth,
    sortOrder: album.sortOrder,
    sortName: album.sortName,
    albumType: album.albumType,
    visibility: (album.visibility || 'public') as any,
  };
}

function convertFirebaseMediaToHybrid(media: FirebaseMediaItem): HybridMediaItem {
  return {
    id: media.id,
    title: media.title,
    description: media.description,
    url: media.url,
    thumbnailUrl: resolveThumbnailUrl(media.thumbnailUrl || media.url),
    width: media.width,
    height: media.height,
    isPanorama: media.isPanorama,
    type: media.type,
    source: 'firebase',
    albumId: media.albumId,
    uploadedAt: media.uploadedAt?.toDate(),
    uploaderName: media.uploaderName,
    tags: media.tags || [],
  };
}

/**
 * Enrich albums with cover images from sub-albums if needed
 */
export async function enrichAlbumsWithCovers(
  firestore: Firestore,
  albums: HybridAlbum[]
): Promise<HybridAlbum[]> {
  const results = await Promise.all(
    albums.map(async (album) => {
      if (album.coverUrl) {
        return album;
      }

      const coverUrl = await findAlbumCoverImage(
        firestore,
        album.id,
        2
      );

      return {
        ...album,
        coverUrl: coverUrl || album.coverUrl
      };
    })
  );

  return results;
}

export async function findAlbumCoverImage(
  firestore: Firestore,
  albumId: string,
  maxDepth: number = 3
): Promise<string | undefined> {
  if (maxDepth === 0) return undefined;

  try {
    const media = await getHybridAlbumMedia(firestore, albumId);

    // Prefer photos, but fall back to video thumbnails
    const firstPhoto = media.find(item => item.type === 'photo');
    if (firstPhoto) {
      return firstPhoto.thumbnailUrl || firstPhoto.url;
    }

    const firstVideo = media.find(item => item.type === 'video');
    if (firstVideo?.thumbnailUrl) {
      return firstVideo.thumbnailUrl;
    }

    // No direct media with usable thumbnails, recurse into sub-albums
    const subAlbums = await getHybridSubAlbums(firestore, albumId);
    for (const subAlbum of subAlbums) {
      const cover = await findAlbumCoverImage(
        firestore,
        subAlbum.id,
        maxDepth - 1
      );
      if (cover) return cover;
    }
  } catch (error) {
    console.error('Error finding album cover image:', error);
  }
  return undefined;
}
